/**
 * `/admin/demo-links` — create + revoke admin-issued demo bypass links.
 *
 * Two kinds:
 *   - `single_use` — one click, one session. Banner copy: "share once, then dead."
 *   - `multi_use`  — link can be reused; each open creates a new session.
 *                    All sessions expire when the link does. Revoking kills
 *                    every live session immediately.
 *
 * The URL containing the raw token is only returned ONCE, at create time.
 * Admin must copy it immediately; the token is hashed at rest.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { DemoLink, DemoLinkKind } from '@eloquor/api-contract';
import { createDemoLink, listDemoLinks, revokeDemoLink } from '../../api/admin';

export function AdminDemoLinksPage() {
  const [links, setLinks] = useState<DemoLink[]>([]);
  const [justCreated, setJustCreated] = useState<DemoLink | null>(null);
  const [kind, setKind] = useState<DemoLinkKind>('single_use');

  const refresh = useCallback(async () => {
    const { links: list } = await listDemoLinks();
    setLinks(list);
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const cardGrant = Number.parseInt(String(form.get('cardGrant') ?? '50'), 10);
    const cardGrantTotal =
      kind === 'multi_use'
        ? Number.parseInt(String(form.get('cardGrantTotal') ?? '500'), 10)
        : undefined;
    const { link } = await createDemoLink({
      label: String(form.get('label') ?? 'Demo'),
      kind,
      cardGrant,
      cardGrantTotal,
      ttlHours: Number.parseInt(String(form.get('ttlHours') ?? '24'), 10),
    });
    setJustCreated(link);
    (e.currentTarget as HTMLFormElement).reset();
    setKind('single_use');
    await refresh();
  }

  async function handleRevoke(l: DemoLink) {
    const msg =
      l.kind === 'multi_use'
        ? 'Revoke this multi-use link? Every live session it spawned will be signed out immediately.'
        : 'Revoke this demo link?';
    if (!confirm(msg)) return;
    await revokeDemoLink(l.id);
    await refresh();
  }

  return (
    <>
      <div className="auth-eyebrow"><span className="num">ADMIN</span> · Demo links</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Demo bypass links
      </h1>
      <p style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 16, maxWidth: 640 }}>
        Spin up ephemeral sessions — no account required, capped card budget, auto-deleted on expiry or revoke.
        <strong> Single-use</strong> links die on first click; <strong>multi-use</strong> links keep working for many users until they expire, all sharing a single pool.
      </p>

      {/*
        Kind radio first so the layout-dependent form below makes sense — the
        previous order let the form reflow under the user as they reached the
        radio at the bottom.
      */}
      <div
        role="radiogroup"
        aria-label="Demo link kind"
        style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--fg-2)', marginBottom: 12 }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="radio"
            name="kind"
            value="single_use"
            checked={kind === 'single_use'}
            onChange={() => setKind('single_use')}
          />
          Single use — one click consumes the link
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="radio"
            name="kind"
            value="multi_use"
            checked={kind === 'multi_use'}
            onChange={() => setKind('multi_use')}
          />
          Multi-use — many users, one shared pool
        </label>
      </div>

      <form
        onSubmit={handleCreate}
        style={{
          display: 'grid',
          gridTemplateColumns: kind === 'multi_use' ? '1.5fr 1fr 1fr 1fr auto' : '2fr 1fr 1fr auto',
          gap: 12,
          alignItems: 'end',
          marginBottom: 24,
          background: 'var(--vellum)',
          padding: 16,
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <div className="field">
          <label className="label">Label</label>
          <input className="input" name="label" required placeholder="Internal Demo" />
        </div>
        <div className="field">
          <label className="label">
            {kind === 'multi_use' ? 'Per-session grant' : 'Card grant'}
          </label>
          <input
            className="input"
            name="cardGrant"
            type="number"
            defaultValue={50}
            min={1}
            title="Cards each session can spend"
          />
        </div>
        {kind === 'multi_use' && (
          <div className="field">
            <label className="label">Shared pool</label>
            <input
              className="input"
              name="cardGrantTotal"
              type="number"
              defaultValue={500}
              min={1}
              title="Combined cap across every session from this link"
            />
          </div>
        )}
        <div className="field">
          <label className="label">TTL hours</label>
          <input className="input" name="ttlHours" type="number" defaultValue={24} min={1} />
        </div>
        <button className="btn-primary" style={{ width: 'auto', margin: 0 }} type="submit">
          Generate
        </button>
      </form>

      {justCreated && (
        <div className="banner" style={{ marginBottom: 16 }}>
          <strong>
            {justCreated.kind === 'multi_use'
              ? 'Copy this URL — anyone you share it with gets their own session. The token is not retrievable again:'
              : 'Copy this URL now — one click consumes it. The token is not retrievable again:'}
          </strong>
          <pre
            style={{
              margin: '8px 0 0',
              padding: 12,
              background: 'var(--paper)',
              border: '1px solid var(--border)',
              fontSize: 12,
              userSelect: 'all',
              overflow: 'auto',
            }}
          >
            {justCreated.url}
          </pre>
          {justCreated.kind === 'multi_use' && (
            <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 8 }}>
              Each session can spend up to <strong>{justCreated.cardGrant}</strong> cards;
              {' '}total spend across all sessions is capped at <strong>{justCreated.cardGrantTotal}</strong>.
              All sessions expire {new Date(justCreated.expiresAt).toLocaleString()}.
            </div>
          )}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '8px 12px' }}>Label</th>
            <th style={{ padding: '8px 12px' }}>Kind</th>
            <th style={{ padding: '8px 12px' }}>Budget</th>
            <th style={{ padding: '8px 12px' }}>Created</th>
            <th style={{ padding: '8px 12px' }}>Expires</th>
            <th style={{ padding: '8px 12px' }}>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {links.map((l) => {
            const expired = new Date(l.expiresAt) <= new Date();
            const poolExhausted = l.cardsConsumed >= l.cardGrantTotal;
            const dead = expired || (l.kind === 'single_use' && l.consumedAt !== null) || poolExhausted;
            const status = expired
              ? 'expired'
              : l.kind === 'single_use'
                ? l.consumedAt
                  ? 'consumed'
                  : 'live'
                : poolExhausted
                  ? 'pool empty'
                  : 'live';
            const budget =
              l.kind === 'single_use'
                ? `${l.cardGrant}`
                : `${l.cardsConsumed} / ${l.cardGrantTotal} used (${l.cardGrant}/session)`;
            return (
              <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 12px' }}>{l.label}</td>
                <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  {l.kind === 'multi_use' ? 'multi' : 'single'}
                </td>
                <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {budget}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--fg-3)' }}>
                  {new Date(l.createdAt).toLocaleString()}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--fg-3)' }}>
                  {new Date(l.expiresAt).toLocaleString()}
                </td>
                <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  {status}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {!dead && (
                    <button
                      className="btn-ghost"
                      style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => handleRevoke(l)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
